<script setup lang="ts">
import { ref } from 'vue';
import { X } from 'lucide-vue-next';

const isMobileMenuOpen = ref(false);

const toggleMobileMenu = () => {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
};

const scrollToSection = (sectionId: string) => {
  const element = document.querySelector(sectionId);
  if (element) {
    isMobileMenuOpen.value = false; // Close mobile menu after clicking
    element.scrollIntoView({ 
      behavior: 'smooth',
      block: 'start'
    });
  }
};
</script>

<template>
  <header class="fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-sm z-50 border-b">
    <div class="container mx-auto px-4 py-4">
      <div class="flex items-center justify-between">
        <a 
          @click.prevent="scrollToSection('#hero')" 
          href="#hero" 
          class="flex items-center space-x-2 cursor-pointer"
        >
          <img src="/logo-bytescape-web.svg" alt="Bytescape Logo" class="h-8 w-8">
          <span class="text-xl font-bold">Bytescape</span>
        </a>
        
        <!-- Desktop Navigation -->
        <nav class="hidden md:flex items-center space-x-8">
          <a 
            @click.prevent="scrollToSection('#services')" 
            href="#services" 
            class="text-gray-800 hover:text-indigo-800 cursor-pointer transition-colors"
          >
            {{ $t('header.services') }}
          </a>
          <a 
            @click.prevent="scrollToSection('#expertise')" 
            href="#expertise" 
            class="text-gray-800 hover:text-indigo-800 cursor-pointer transition-colors"
          >
            {{ $t('header.expertise') }}
          </a>
          <a 
            @click.prevent="scrollToSection('#contact')" 
            href="#contact" 
            class="text-gray-800 hover:text-indigo-800 cursor-pointer transition-colors"
          >
            {{ $t('header.contact') }}
          </a>
          <a 
            href="https://calendly.com/bytescape-sh/30min" 
            target="_blank" 
            rel="noopener noreferrer"
            class="inline-block"
          >
            <button class="text-white bg-[#16A7FF] px-4 py-2 rounded-full border-2 border-transparent hover:border-[#096DA8] hover:bg-[#096DA8] transition">
              {{ $t('header.scheduleCall') }}
            </button>
          </a>
        </nav>
        
        <!-- Mobile Menu Button -->
        <button 
          @click="toggleMobileMenu"
          class="md:hidden p-2 text-gray-600 hover:text-indigo-600 transition-colors"
          aria-label="Toggle mobile menu"
        >
          <svg v-if="!isMobileMenuOpen" class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
          </svg>
          <X v-else class="w-6 h-6" />
        </button>
      </div>
    </div>

    <!-- Mobile Menu with Transition -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out"
      enter-from-class="opacity-0 -translate-y-full"
      enter-to-class="opacity-100 translate-y-0"
      leave-active-class="transition-all duration-200 ease-in"
      leave-from-class="opacity-100 translate-y-0"
      leave-to-class="opacity-0 -translate-y-full"
    >
      <div 
        v-show="isMobileMenuOpen"
        class="md:hidden fixed top-[73px] right-0 bg-white z-40 shadow-lg"
      >
        <nav class="container mx-auto px-4 py-6 flex flex-col space-y-4">
          <a 
            @click="scrollToSection('#services')" 
            href="#services"
            class="text-gray-600 hover:text-indigo-600 text-lg py-2 transition-colors"
          >
            {{ $t('header.services') }}
          </a>
          <a 
            @click="scrollToSection('#expertise')" 
            href="#expertise"
            class="text-gray-600 hover:text-indigo-600 text-lg py-2 transition-colors"
          >
            {{ $t('header.expertise') }}
          </a>
          <a 
            @click="scrollToSection('#contact')" 
            href="#contact"
            class="text-gray-600 hover:text-indigo-600 text-lg py-2 transition-colors"
          >
            {{ $t('header.contact') }}
          </a>
          <button class="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition w-full">
            {{ $t('header.getQuote') }}
          </button>
        </nav>
      </div>
    </Transition>
  </header>
</template>